import React, { useState, useEffect } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  TextField,
  Select,
  DataTable,
  Badge,
  Button,
  Modal,
  SkeletonBodyText,
} from "@shopify/polaris";
import { TIERS, TIER_NAMES, TIER_COLORS } from "../../shared/types.js";
import { getCustomers, adjustPoints } from "../lib/api.js";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");

  useEffect(() => {
    loadCustomers();
  }, [tierFilter]);

  async function loadCustomers() {
    setLoading(true);
    try {
      const data = await getCustomers({ tier: tierFilter || undefined, q: search || undefined });
      setCustomers(data.customers || []);
    } catch {
      // Mock data for development
      setCustomers(getMockCustomers());
    } finally {
      setLoading(false);
    }
  }

  function handleSearch() {
    loadCustomers();
  }

  async function handleAdjustPoints() {
    if (!selectedCustomer || !adjustAmount) return;
    try {
      await adjustPoints(selectedCustomer.id, parseInt(adjustAmount, 10), adjustReason);
      setAdjustModalOpen(false);
      setAdjustAmount("");
      setAdjustReason("");
      loadCustomers();
    } catch (error) {
      console.error("Point adjustment error:", error);
    }
  }

  const tierOptions = [
    { label: "Tum Seviyeler", value: "" },
    { label: "Bronz", value: "bronze" },
    { label: "Gumus", value: "silver" },
    { label: "Altin", value: "gold" },
  ];

  const rows = customers.map((c) => [
    c.displayName,
    c.email,
    <TierBadge tier={c.loyalty.tier} />,
    new Intl.NumberFormat("tr-TR").format(c.loyalty.pointsBalance),
    `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 0 }).format(c.loyalty.totalSpent)} TL`,
    c.loyalty.badges?.length || 0,
    <Button
      size="slim"
      onClick={() => {
        setSelectedCustomer(c);
        setAdjustModalOpen(true);
      }}
    >
      Puan Duzenle
    </Button>,
  ]);

  return (
    <Page title="Musteriler" subtitle="Loyalty uyeleri ve puan yonetimi">
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="300" blockAlign="end">
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Musteri Ara"
                    value={search}
                    onChange={setSearch}
                    placeholder="Isim veya email..."
                    autoComplete="off"
                    onBlur={handleSearch}
                  />
                </div>
                <div style={{ width: 200 }}>
                  <Select
                    label="Tier Filtre"
                    options={tierOptions}
                    value={tierFilter}
                    onChange={setTierFilter}
                  />
                </div>
                <Button onClick={handleSearch}>Ara</Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            {loading ? (
              <SkeletonBodyText lines={10} />
            ) : (
              <DataTable
                columnContentTypes={["text", "text", "text", "numeric", "numeric", "numeric", "text"]}
                headings={["Isim", "Email", "Seviye", "Puan", "Toplam Harcama", "Rozetler", "Islem"]}
                rows={rows}
                hoverable
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Adjust Points Modal */}
      <Modal
        open={adjustModalOpen}
        onClose={() => setAdjustModalOpen(false)}
        title={`Puan Duzenle: ${selectedCustomer?.displayName || ""}`}
        primaryAction={{
          content: "Uygula",
          onAction: handleAdjustPoints,
        }}
        secondaryActions={[
          { content: "Iptal", onAction: () => setAdjustModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text>
              Mevcut Puan: <strong>{selectedCustomer?.loyalty?.pointsBalance || 0}</strong>
            </Text>
            <TextField
              label="Puan Miktari (pozitif: ekle, negatif: cikar)"
              type="number"
              value={adjustAmount}
              onChange={setAdjustAmount}
              autoComplete="off"
              helpText="Ornek: 100 eklemek icin '100', 50 cikarmak icin '-50'"
            />
            <TextField
              label="Neden"
              value={adjustReason}
              onChange={setAdjustReason}
              autoComplete="off"
              placeholder="Manuel duzeltme, kampanya odulu, vb."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

function TierBadge({ tier }) {
  const toneMap = {
    bronze: undefined,
    silver: "info",
    gold: "warning",
  };
  return (
    <Badge tone={toneMap[tier]}>
      {TIER_NAMES[tier] || "Bronz"}
    </Badge>
  );
}

function getMockCustomers() {
  return [
    { id: "gid://shopify/Customer/1", displayName: "Ahmet Yilmaz", email: "ahmet@example.com", loyalty: { pointsBalance: 480, totalSpent: 32000, tier: "silver", badges: [{ id: "1" }, { id: "2" }] } },
    { id: "gid://shopify/Customer/2", displayName: "Mehmet Kaya", email: "mehmet@example.com", loyalty: { pointsBalance: 1120, totalSpent: 89000, tier: "gold", badges: [{ id: "1" }, { id: "2" }, { id: "3" }] } },
    { id: "gid://shopify/Customer/3", displayName: "Ayse Demir", email: "ayse@example.com", loyalty: { pointsBalance: 150, totalSpent: 8500, tier: "bronze", badges: [{ id: "1" }] } },
    { id: "gid://shopify/Customer/4", displayName: "Fatma Sahin", email: "fatma@example.com", loyalty: { pointsBalance: 800, totalSpent: 45000, tier: "silver", badges: [{ id: "1" }, { id: "2" }] } },
    { id: "gid://shopify/Customer/5", displayName: "Can Bulut", email: "can@example.com", loyalty: { pointsBalance: 60, totalSpent: 3200, tier: "bronze", badges: [] } },
  ];
}
